import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { MdDownloadForOffline } from 'react-icons/md';
import { FaRegComment } from 'react-icons/fa';
import { AiTwotoneDelete, AiFillHeart, AiOutlineHeart } from 'react-icons/ai';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { IoMdShare } from 'react-icons/io';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

import { client, urlFor } from '../client';

const Pin = ({ pin }) => {
  const [savingPost, setSavingPost] = useState(false);
  const [localSaves, setLocalSaves] = useState(pin?.save || []);
  const [menuOpen, setMenuOpen] = useState(false);
  const [localLikes, setLocalLikes] = useState(pin.likes || []);
  const [likingPost, setLikingPost] = useState(false);

  const navigate = useNavigate();

  const { postedBy, image, _id, title } = pin;

  const user = localStorage.getItem('user') && localStorage.getItem('user') !== 'undefined'
    ? JSON.parse(localStorage.getItem('user'))
    : null;

  useEffect(() => {
    if (pin.likes) {
      setLocalLikes(pin.likes);
    }
  }, [pin.likes]);

  const alreadySaved = localSaves?.some(
    (item) => item?.postedBy?._id === user?._id
  );

  const alreadyLiked = localLikes?.some((like) => like?._id === user?._id);

  const deletePin = (id) => {
    client
      .delete(id)
      .then(() => {
        window.location.reload();
      })
      .catch((err) => {
        toast.error('Failed to delete post!!');
        console.error(err);
      });
  };

  const savePin = (id) => {
    setSavingPost(true);

    if (alreadySaved) {
      const userSave = localSaves.find(
        (item) => item?.postedBy?._id === user?._id
      );
      client
        .patch(id)
        .unset([`save[_key=="${userSave._key}"]`])
        .commit()
        .then((updatedPin) => {
          toast.success('Post unsaved!');
          setLocalSaves(updatedPin.save);
          setSavingPost(false);
          window.dispatchEvent(new Event('pin-saved'));
        })
        .catch(() => {
          toast.error('Failed to unsave post..');
          setSavingPost(false);
        });
    } else {
      client
        .patch(id)
        .setIfMissing({ save: [] })
        .insert('after', 'save[-1]', [{
          _key: uuidv4(),
          userId: user?._id,
          _type: 'save',
          postedBy: {
            _type: 'postedBy',
            _id: user?._id,
          },
        }])
        .commit()
        .then((updatedPin) => {
          toast.success('Post saved!');
          setLocalSaves(updatedPin.save);
          setSavingPost(false);
          window.dispatchEvent(new Event('pin-saved'));
        })
        .catch(() => {
          toast.error('Failed to save post..');
          setSavingPost(false);
        });
    }
  };

  // Like/Unlike logic
  const toggleLike = async (id) => {
    if (!user) {
      toast.error('Please login to like posts!');
      return;
    }
    setLikingPost(true);

    const alreadyLiked = localLikes?.some((like) => like?._id === user?._id);

    if (alreadyLiked) {
      // Unlike
      client
        .patch(id)
        .unset([`likes[_id=="${user._id}"]`])
        .commit()
        .then((updatedPin) => {
          setLocalLikes(updatedPin.likes || []);
          toast.success('Like removed');
          setLikingPost(false);
        })
        .catch(() => {
          toast.error('Failed to unlike');
          setLikingPost(false);
        });
    } else {
      // Like
      client
        .patch(id)
        .setIfMissing({ likes: [] })
        .insert('after', 'likes[-1]', [{
          _id: user._id,
          userName: user.userName,
        }])
        .commit()
        .then((updatedPin) => {
          setLocalLikes(updatedPin.likes || []);
          toast.success('Liked!');
          setLikingPost(false);
        })
        .catch(() => {
          toast.error('Failed to like');
          setLikingPost(false);
        });
    }
  };

  const sharePin = (e) => {
    e.stopPropagation();
    const shareData = {
      title: 'Check out this post!',
      text: 'I found this cool post on Imagera',
      url: `${window.location.origin}/pin-detail/${_id}`,
    };

    if (navigator.share) {
      navigator.share(shareData)
        .then(() => toast.success('Post shared!'))
        .catch((err) => console.log('Error sharing:', err));
    } else {
      navigator.clipboard.writeText(shareData.url)
        .then(() => toast.success('Link copied to clipboard!'))
        .catch(() => toast.error('Failed to copy link!'));
    }
  };

  return (
    <div className="overflow-y-auto hide-scrollbar">
      <div className="m-4 max-w-[22rem] bg-gradient-to-br from-gray-100 via-blue-100 to-purple-100 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900 rounded-2xl shadow-2xl border border-blue-400/20 flex flex-col overflow-hidden transition-all duration-300 hover:shadow-2xl">
        {/* Header */}
        <div className="relative flex items-center gap-2 px-4 pt-3 pb-3 bg-gradient-to-r from-blue-900 via-purple-900 to-blue-700 dark:from-blue-900 dark:via-purple-900 dark:to-blue-700 rounded-t-2xl">
          <Link to={`/user-profile/${postedBy?._id}`} className="flex items-center gap-2">
            <img
              className="w-9 h-9 rounded-full object-cover border-2 border-blue-400 shadow"
              src={postedBy?.image}
              alt="user-profile"
            />
            <span className="font-semibold text-white capitalize drop-shadow">{postedBy?.userName}</span>
          </Link>
          <button
            className="ml-auto p-2 rounded-full hover:bg-blue-800/60 transition"
            onClick={() => setMenuOpen((v) => !v)}
            type="button"
            title="More options"
          >
            <BsThreeDotsVertical className="text-xl text-gray-300" />
          </button>
          {menuOpen && (
            <div className="absolute right-4 top-14 z-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-blue-400/20 min-w-[160px] py-2">
              <a
                href={`${image?.asset?.url}?dl=`}
                download
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-900 cursor-pointer"
              >
                <MdDownloadForOffline className="text-lg" />
                Download
              </a>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  savePin(_id);
                  setMenuOpen(false);
                }}
                disabled={savingPost}
                className="flex items-center gap-2 px-4 py-2 w-full text-gray-700 dark:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-900 cursor-pointer"
              >
                {savingPost
                  ? (alreadySaved ? 'Unsaving...' : 'Saving...')
                  : (alreadySaved ? 'Unsave' : 'Save')}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  sharePin(e);
                  setMenuOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2 w-full text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900 cursor-pointer"
              >
                <IoMdShare className="text-lg" />
                Share
              </button>
              {postedBy?._id === user?._id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deletePin(_id);
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 w-full text-red-600 hover:bg-blue-100 dark:hover:bg-blue-900 cursor-pointer"
                >
                  <AiTwotoneDelete className="text-lg" />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>

        {/* Image */}
        <div
          onClick={() => navigate(`/pin-detail/${_id}`)}
          className="relative cursor-pointer w-full bg-white/80 dark:bg-gray-900/80 flex items-center justify-center"
          style={{ aspectRatio: '1/1', minHeight: 0 }}
        >
          {image && (
            <img
              loading="lazy"
              className="block w-full h-full object-contain rounded-xl shadow"
              src={urlFor(image).width(400).url()}
              alt={title || "user-post"}
              style={{ maxHeight: '350px' }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 pb-3 pt-3 border-t border-blue-200 dark:border-blue-900 bg-white/90 dark:bg-gray-900/90">
          <h3 className="font-semibold text-md text-gray-900 dark:text-white truncate">
            {title && title.trim() !== '' ? title : 'Untitled Post'}
          </h3>
          <div className="flex items-center gap-2">
            <motion.button
              whileTap={{ scale: 1.1 }}
              onClick={(e) => {
                e.stopPropagation();
                toggleLike(_id);
              }}
              disabled={likingPost}
              className={`flex items-center gap-1 px-2 py-1 rounded-full font-semibold text-base transition
                ${alreadyLiked ? 'bg-pink-500 text-white' : 'bg-gray-200 text-pink-600 dark:bg-gray-800 dark:text-pink-400'}
                ${likingPost ? 'opacity-50 cursor-not-allowed' : 'hover:bg-pink-100 dark:hover:bg-pink-900'}
              `}
              style={{
                minWidth: '50px',
                justifyContent: 'center'
              }}
              title={alreadyLiked ? "Unlike" : "Like"}
              type="button"
            >
              {alreadyLiked ? (
                <AiFillHeart className="text-lg" />
              ) : (
                <AiOutlineHeart className="text-lg" />
              )}
              <span className="font-bold">{localLikes?.length}</span>
            </motion.button>
            <button
              onClick={() => navigate(`/pin-detail/${_id}#comments`)}
              className="text-gray-600 dark:text-gray-300 hover:text-blue-500 transition-transform duration-200 bg-gray-200 dark:bg-gray-700 rounded-full p-2"
              title="Comments"
              type="button"
            >
              <FaRegComment className="text-lg" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pin;